const ORG_DOMAIN_SUFFIX = '.gix.net.br';
const LOCAL_ORG_DOMAIN_SUFFIX = '.gix.com';
const DEV_TEST_DOMAIN = 'dev.gix.com';

function isLocalDevEnvironment() {
  return window.location.hostname.endsWith('.gix.com');
}

// Diz se o valor de Org#env indica uma org local/dev (setado na tela de
// gerenciamento de Orgs). Igualdade EXATA — não usa prefixo/regex, pra
// nunca pegar orgs de produção por acidente (ex: "production_defensor"
// nunca deve bater aqui).
function isLocalEnvValue(env) {
  if (!env) return false;
  const normalized = env.trim().toLowerCase();
  return normalized === 'development' || normalized === 'dev';
}

// A org decide seu próprio domínio via Org#env: se o env indicar
// local/dev, resolve pra .gix.com. Caso contrário, é sempre produção
// (.gix.net.br) — não depende mais de onde o React está sendo acessado,
// senão orgs reais viravam .gix.com só por acidente do host do React.
function orgDomainSuffix(env) {
  return isLocalEnvValue(env) ? LOCAL_ORG_DOMAIN_SUFFIX : ORG_DOMAIN_SUFFIX;
}

export function buildOrgDomain(subdomain, env) {
  if (!subdomain) return null;
  return `${subdomain}${orgDomainSuffix(env)}`;
}

export function buildPlantoesDomain(subdomain, env) {
  if (!subdomain) return null;
  return `${subdomain}plantoes${orgDomainSuffix(env)}`;
}

export function parseJwt(token) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(window.atob(base64))));
  } catch (err) {
    console.error('JWT parse error', err);
    return {};
  }
}

// Decide http: vs https: pro domínio de uma org. Domínios locais (.gix.com,
// ou dev.gix.com) não têm certificado TLS, então precisam de http:.
// Exportada pra ser reaproveitada em qualquer chamada de API pra domínio
// de org — não só o buildSsoCallbackUrl.
export function getProtocolForDomain(orgDomain) {
  const isLocalDomain = orgDomain?.endsWith(LOCAL_ORG_DOMAIN_SUFFIX) || orgDomain === DEV_TEST_DOMAIN;
  return isLocalDevEnvironment() || isLocalDomain ? 'http:' : 'https:';
}

export function buildSsoCallbackUrl(orgDomain, path = '/') {
  const token = localStorage.getItem('serviceToken');
  const protocol = getProtocolForDomain(orgDomain);

  // Ensure path always starts with a single leading slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const redirectUrl = `${protocol}//${orgDomain}${normalizedPath}`;

  if (!token) {
    return `${protocol}//${orgDomain}/users/sign_in`;
  }

  return `${protocol}//${orgDomain}/users/sso_callback?jwt=${encodeURIComponent(token)}&redirect_url=${encodeURIComponent(redirectUrl)}`;
}

function buildAccessList(user, domainBuilder) {
  const orgs = Array.isArray(user?.orgs) ? user.orgs : [];

  return orgs
    .filter((org) => org?.active !== false)
    .map((org) => ({
      id: org.id,
      name: org.name,
      subdomain: org.subdomain,
      domain: domainBuilder(org.subdomain, org.env)
    }))
    .filter((org) => Boolean(org.domain));
}

export function getContractsOrgList(user) {
  const orgs = Array.isArray(user?.orgs) ? user.orgs : [];

  return orgs
    .filter((org) => org?.active !== false && !org.subdomain?.startsWith('temp-'))
    .map((org) => ({
      id: org.id,
      name: org.name,
      subdomain: org.subdomain,
      domain: buildOrgDomain(org.subdomain, org.env)
    }))
    .filter((org) => Boolean(org.domain));
}

export function getOrgAccessList(user) {
  return buildAccessList(user, buildOrgDomain);
}

// Menu "Empresas": a mesma org, acessada pelo subdominio alternativo (ex: temp-softmed).
export function getEmpresasAccessList(user) {
  const orgs = Array.isArray(user?.orgs) ? user.orgs : [];

  return orgs
    .filter((org) => org?.active !== false && org.alt_subdomain)
    .map((org) => ({
      id: org.id,
      name: org.name,
      subdomain: org.alt_subdomain,
      domain: buildOrgDomain(org.alt_subdomain, org.env)
    }))
    .filter((org) => Boolean(org.domain));
}

export function getPlantoesAccessList(user) {
  return buildAccessList(user, buildPlantoesDomain);
}